import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";
import { DynIcon } from "@/components/DynIcon";
import { NAV_LINKS, PRODUCTS } from "@/lib/mockData";

const linkBase =
  "relative text-[0.95rem] font-medium text-slatey hover:text-ink transition-colors duration-200 py-1";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  const go = (to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-panel border-b border-stroke shadow-sm" : "bg-white/0 border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-16 items-center justify-between">
          <Logo />

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-9">
            <button
              data-testid="nav-home"
              onClick={() => go("/")}
              className={`${linkBase} group`}
            >
              Home
              <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-brand rounded-full transition-all duration-300 group-hover:w-full" />
            </button>

            {/* Products dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button
                data-testid="nav-products"
                aria-haspopup="true"
                aria-expanded={productsOpen}
                onClick={() => setProductsOpen((o) => !o)}
                className={`${linkBase} inline-flex items-center gap-1`}
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform ${productsOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {productsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute left-1/2 top-full w-80 -translate-x-1/2 pt-3"
                    data-testid="products-dropdown"
                  >
                    <div className="rounded-2xl border border-stroke bg-white p-2 shadow-float">
                      {PRODUCTS.map((p) => (
                        <a
                          key={p.name}
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`product-link-${p.name.toLowerCase()}`}
                          className="group flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-brand-tint"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                            <DynIcon name={p.icon} className="h-4.5 w-4.5" />
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-1 text-sm font-semibold text-ink">
                              {p.name}
                              <ExternalLink className="h-3 w-3 text-slatey opacity-0 transition-opacity group-hover:opacity-100" />
                            </span>
                            <span className="block text-xs text-slatey">{p.tag}</span>
                          </span>
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => go(l.to)}
                className={`${linkBase} group`}
              >
                {l.label}
                <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-brand rounded-full transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              data-testid="header-signin"
              className="px-4 py-2 rounded-lg text-[0.95rem] font-medium text-ink hover:bg-brand-tint transition-colors duration-200"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              data-testid="header-get-started"
              className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[0.95rem] font-semibold text-white bg-brand hover:bg-brand-hover shadow-sm transition-all duration-200"
            >
              Get Started
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-stroke bg-white text-ink"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-drawer"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-b border-stroke"
          >
            <nav aria-label="Mobile" className="px-5 py-5 flex flex-col gap-1">
              <button
                data-testid="mobile-nav-home"
                onClick={() => go("/")}
                className="text-left px-3 py-3 rounded-lg text-base font-medium text-ink hover:bg-canvas transition-colors"
              >
                Home
              </button>
              {/* Products expandable */}
              <button
                data-testid="mobile-nav-products"
                onClick={() => setMobileProducts((o) => !o)}
                className="flex items-center justify-between px-3 py-3 rounded-lg text-base font-medium text-ink hover:bg-canvas transition-colors"
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform ${mobileProducts ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mobileProducts && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden pl-3"
                  >
                    {PRODUCTS.map((p) => (
                      <a
                        key={p.name}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`mobile-product-link-${p.name.toLowerCase()}`}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-slatey hover:bg-canvas"
                      >
                        <DynIcon name={p.icon} className="h-4 w-4 text-brand" />
                        {p.name}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {NAV_LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => go(l.to)}
                  className="text-left px-3 py-3 rounded-lg text-base font-medium text-ink hover:bg-canvas transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-3 flex flex-col gap-2.5">
                <Link to="/login" className="w-full text-center px-4 py-3 rounded-lg font-medium text-ink border border-stroke">
                  Sign In
                </Link>
                <Link to="/signup" className="w-full text-center px-4 py-3 rounded-lg font-semibold text-white bg-brand">
                  Get Started →
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
